/*
 Convention: https://docs.google.com/document/d/1ePqfM41II2CNBYpTuOgjLZafRkn6vABQ5NF40JGER1E/edit
 Review Reference: https://docs.google.com/document/d/1Diu8ZwKXPOWCsH2ry8rufCYADGFcHtR4MY3k1c3Ypes/edit

 ! Review
 TODO Dashboard - use SWR
 */

import update from 'immutability-helper';
import {
  ChartAccountData,
  IntegrationPlatformType,
  MappingData,
} from 'src/models';
import { ActionType, defaultAction } from 'src/redux/types';
import { reportFunctionError } from 'src/utilities/sentry';
import { ACTION_TYPES } from '../types';

export type FetchConnectionStatusState = {
  data: IntegrationPlatformType | null;
  loading: boolean;
  error: string;
};

export type SubmitRedirectState = {
  loading: boolean;
  isSuccess: boolean;
  error: string;
};

export type FetchChartOfAccountState = {
  data: ChartAccountData[];
  loading: boolean;
  isSuccess: boolean;
  error: string;
};

export type FetchProductMappingState = {
  data: MappingData[];
  loading: boolean;
  isSuccess: boolean;
  error: string;
};

export type FetchTaxMappingState = {
  data: MappingData;
  loading: boolean;
  isSuccess: boolean;
  error: string;
};

export type SubmitProductMapping = {
  loading: boolean;
  isSuccess: boolean;
  error: string;
};

export type DeleteProductMapping = {
  loading: boolean;
  isSuccess: boolean;
  error: string;
};

export type IntegrationState = {
  fetchStatus: FetchConnectionStatusState;
  submitRedirect: SubmitRedirectState;
  fetchChart: FetchChartOfAccountState;
  fetchProductMapping: FetchProductMappingState;
  fetchTaxMapping: FetchTaxMappingState;
  submitProductMapping: SubmitProductMapping;
  submitTaxMapping: SubmitProductMapping;
  deleteProductMapping: DeleteProductMapping;
};

const initialState: IntegrationState = {
  fetchStatus: {
    data: null,
    loading: true,
    error: '',
  },
  submitRedirect: {
    loading: true,
    isSuccess: false,
    error: '',
  },
  fetchChart: {
    data: [] as ChartAccountData[],
    loading: true,
    isSuccess: false,
    error: '',
  },
  fetchProductMapping: {
    data: [],
    loading: true,
    isSuccess: false,
    error: '',
  },
  fetchTaxMapping: {
    data: {
      account_code: null,
      account_id: '',
      account_name: '',
      product_name: '',
    },
    loading: true,
    isSuccess: false,
    error: '',
  },
  submitProductMapping: {
    loading: false,
    isSuccess: false,
    error: '',
  },
  submitTaxMapping: {
    loading: false,
    isSuccess: true,
    error: '',
  },
  deleteProductMapping: {
    loading: false,
    isSuccess: false,
    error: '',
  },
};

export default (state = initialState, action: ActionType = defaultAction) => {
  const { payload, type } = action;
  switch (type) {
    case ACTION_TYPES.CONNECTION_STATUS_FETCH:
      return update(state, {
        fetchStatus: {
          loading: {
            $set: true,
          },
        },
      });
    case ACTION_TYPES.CONNECTION_STATUS_FETCH_SUCCESS:
      return update(state, {
        fetchStatus: {
          $merge: {
            data: payload.data.platform,
            loading: false,
          },
        },
      });
    case ACTION_TYPES.CONNECTION_STATUS_FETCH_FAILED:
      reportFunctionError({
        domain: 'dashboard',
        message: 'Failed to get integration connection status',
        name: 'getConnectionStatus',
        exception: payload,
      });
      return update(state, {
        fetchStatus: {
          $merge: {
            data: null,
            loading: false,
          },
        },
      });

    case ACTION_TYPES.REDIRECT_SUBMIT:
      return update(state, {
        submitRedirect: {
          loading: {
            $set: true,
          },
        },
      });
    case ACTION_TYPES.REDIRECT_SUBMIT_SUCCESS:
      return update(state, {
        submitRedirect: {
          $merge: {
            loading: false,
            isSuccess: payload.status_code === 200,
          },
        },
      });
    case ACTION_TYPES.REDIRECT_SUBMIT_FAILED:
      reportFunctionError({
        domain: 'dashboard',
        message: 'Failed to connect integration',
        name: 'redirectIntegration',
        exception: payload,
      });
      return update(state, {
        submitRedirect: {
          $merge: {
            loading: false,
            isSuccess: false,
          },
        },
      });

    case ACTION_TYPES.CHART_OF_ACCOUNT_FETCH:
      return update(state, {
        fetchChart: {
          loading: {
            $set: true,
          },
        },
      });
    case ACTION_TYPES.CHART_OF_ACCOUNT_FETCH_SUCCESS:
      return update(state, {
        fetchChart: {
          $merge: {
            data: payload.data,
            loading: false,
            isSuccess: true,
          },
        },
      });
    case ACTION_TYPES.CHART_OF_ACCOUNT_FETCH_FAILED:
      reportFunctionError({
        domain: 'dashboard',
        message: 'Failed to get integration chart of account',
        name: 'getChartOfAccount',
        exception: payload,
      });
      return update(state, {
        fetchChart: {
          $merge: {
            loading: false,
            isSuccess: false,
          },
        },
      });

    case ACTION_TYPES.PRODUCT_MAPPING_FETCH:
      return update(state, {
        fetchProductMapping: {
          loading: {
            $set: true,
          },
        },
      });
    case ACTION_TYPES.PRODUCT_MAPPING_FETCH_SUCCESS:
      return update(state, {
        fetchProductMapping: {
          $merge: {
            data: payload.data.product_mapping,
            loading: false,
            isSuccess: true,
          },
        },
      });
    case ACTION_TYPES.PRODUCT_MAPPING_FETCH_FAILED:
      reportFunctionError({
        domain: 'dashboard',
        message: 'Failed to get integration product mapping',
        name: 'getProductMapping',
        exception: payload,
      });
      return update(state, {
        fetchProductMapping: {
          $merge: {
            loading: false,
            isSuccess: false,
          },
        },
      });

    case ACTION_TYPES.TAX_MAPPING_FETCH:
      return update(state, {
        fetchTaxMapping: {
          loading: {
            $set: true,
          },
        },
      });
    case ACTION_TYPES.TAX_MAPPING_FETCH_SUCCESS:
      return update(state, {
        fetchTaxMapping: {
          $merge: {
            data: payload.data,
            loading: false,
            isSuccess: true,
          },
        },
      });
    case ACTION_TYPES.TAX_MAPPING_FETCH_FAILED:
      reportFunctionError({
        domain: 'dashboard',
        message: 'Failed to get integration tax mapping',
        name: 'getTaxMapping',
        exception: payload,
      });
      return update(state, {
        fetchTaxMapping: {
          $merge: {
            loading: false,
            isSuccess: false,
          },
        },
      });

    case ACTION_TYPES.PRODUCT_MAPPING_SUBMIT:
      return update(state, {
        submitProductMapping: {
          loading: {
            $set: true,
          },
        },
      });
    case ACTION_TYPES.PRODUCT_MAPPING_SUBMIT_SUCCESS:
      return update(state, {
        submitProductMapping: {
          $merge: {
            loading: false,
            isSuccess: payload.status_code === 200,
          },
        },
      });
    case ACTION_TYPES.PRODUCT_MAPPING_SUBMIT_FAILED:
      reportFunctionError({
        domain: 'dashboard',
        message: 'Failed to submit product mapping for integration',
        name: 'submitProductMapping',
        exception: payload,
      });
      return update(state, {
        submitProductMapping: {
          $merge: {
            loading: false,
            isSuccess: false,
          },
        },
      });

    case ACTION_TYPES.TAX_MAPPING_SUBMIT:
      return update(state, {
        submitTaxMapping: {
          loading: {
            $set: true,
          },
        },
      });
    case ACTION_TYPES.TAX_MAPPING_SUBMIT_SUCCESS:
      return update(state, {
        submitTaxMapping: {
          $merge: {
            loading: false,
            isSuccess: payload.status_code === 200,
          },
        },
      });
    case ACTION_TYPES.TAX_MAPPING_SUBMIT_FAILED:
      reportFunctionError({
        domain: 'dashboard',
        message: 'Failed to submit tax mapping for integration',
        name: 'submitTaxMapping',
        exception: payload,
      });
      return update(state, {
        submitTaxMapping: {
          $merge: {
            loading: false,
            isSuccess: false,
          },
        },
      });

    case ACTION_TYPES.PRODUCT_MAPPING_DELETE:
      return update(state, {
        deleteProductMapping: {
          loading: {
            $set: true,
          },
        },
      });
    case ACTION_TYPES.PRODUCT_MAPPING_DELETE_SUCCESS:
      return update(state, {
        deleteProductMapping: {
          $merge: {
            loading: false,
            isSuccess: payload.status_code === 200,
          },
        },
      });
    case ACTION_TYPES.PRODUCT_MAPPING_DELETE_FAILED:
      reportFunctionError({
        domain: 'dashboard',
        message: 'Failed to delete product mapping for integration',
        name: 'deleteProductMapping',
        exception: payload,
      });
      return update(state, {
        deleteProductMapping: {
          $merge: {
            loading: false,
            isSuccess: false,
          },
        },
      });

    default:
      return state;
  }
};
